import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSystemGraphStatsRelayQuery as UseSystemGraphStatsRelayQuery } from './__generated__/useSystemGraphStatsRelayQuery.graphql';
import type { SystemStatsGranularity } from './__generated__/useSystemGraphStatsRelayQuery.graphql';
import { IDashboardGraph } from '../../../../ts/interface/dashboardGraph.interface';
import { IDashboardStat } from '../../../../ts/interface/dashboardStats.interface';
import { toDashboardStat } from './toDashboardStat';

const SystemGraphStatsQuery = graphql`
  query useSystemGraphStatsRelayQuery(
    $systemId: ID!
    $startDate: DateTime
    $endDate: DateTime
    $granularity: SystemStatsGranularity!
  ) {
    systemStats(
      systemId: $systemId
      startDate: $startDate
      endDate: $endDate
      granularity: $granularity
    ) {
      activeRentalsCount
      rentalsCount
      usersCount
      availableHardwareCount
      rentalsByPeriod {
        count
        period
      }
      usersByPeriod {
        count
        period
      }
      averageDurationByPeriod {
        averageDuration
        period
      }
    }
  }
`;

export interface SystemGraphStatsQueryParams {
  systemId: string;
  startDate?: string;
  endDate?: string;
  granularity?: string;
  enabled?: boolean;
}

/**
 * Map component granularity ('month'/'year') to the GraphQL enum (DAY/MONTH).
 * 'month' view = daily breakdown → DAY
 * 'year' view = monthly breakdown → MONTH
 */
function toGqlGranularity(gran?: string): SystemStatsGranularity {
  return gran === 'year' ? 'MONTH' : 'DAY';
}

/**
 * Parse a period string like "2025-01" or "2025-01-15" into day/month number.
 */
function parsePeriod(period: string, gran?: string): number {
  const parts = period.split('-');
  if (gran === 'year') {
    return parseInt(parts[1], 10); // month number 1-12
  }
  return parseInt(parts[2], 10); // day number 1-31
}

/**
 * Transform period stats [{count, period}] into the format the dashboard
 * components expect: [{count, day}] for DAY or [{count, month}] for MONTH.
 */
function transformPeriodStats(
  items: ReadonlyArray<{ readonly count: number; readonly period: string }>,
  gran?: string
): any[] {
  return items.map((item) => {
    const num = parsePeriod(item.period, gran);
    if (gran === 'year') {
      return { count: item.count, month: num };
    }
    return { count: item.count, day: num };
  });
}

/**
 * Build the durations object the dashboard expects.
 * Components read durations.total_rentals and durations.total_duration.
 * durationAvg is computed as total_duration / total_rentals (in minutes).
 *
 * averageDuration from the API is in seconds, so we convert to minutes.
 */
function buildDurations(
  rentalsByPeriod: ReadonlyArray<{
    readonly count: number;
    readonly period: string;
  }>,
  durationsByPeriod: ReadonlyArray<{
    readonly averageDuration: number;
    readonly period: string;
  }>
): { [key: string]: number } {
  const totalRentals = rentalsByPeriod.reduce((sum, item) => sum + item.count, 0);
  if (!totalRentals) return {};

  // Build a lookup of average duration (seconds) by period
  const durationByPeriod: Record<string, number> = {};
  durationsByPeriod.forEach((item) => {
    durationByPeriod[item.period] = item.averageDuration;
  });

  // Weighted total: sum(avgSeconds[i] * count[i]), then convert to minutes
  const weightedTotalSeconds = rentalsByPeriod.reduce((sum, item) => {
    const avgSec = durationByPeriod[item.period] || 0;
    return sum + avgSec * item.count;
  }, 0);

  const totalDurationMinutes = weightedTotalSeconds / 60;

  return {
    total_rentals: totalRentals,
    total_duration: totalDurationMinutes,
  };
}

/**
 * Transform duration period stats into {count, day/month}[] for charting,
 * converting averageDuration from seconds to minutes.
 */
function transformDurationPeriods(
  items: ReadonlyArray<{
    readonly averageDuration: number;
    readonly period: string;
  }>,
  gran?: string
): any[] {
  return items.map((item) => {
    const num = parsePeriod(item.period, gran);
    const minutes = Math.round(item.averageDuration / 60);
    if (gran === 'year') {
      return { count: minutes, month: num };
    }
    return { count: minutes, day: num };
  });
}

export function useSystemGraphStatsRelay(params: SystemGraphStatsQueryParams) {
  const { systemId, startDate, endDate, granularity, enabled = true } = params;
  const [data, setData] = useState<IDashboardGraph | null>(null);
  // Counter tiles, from the same response as the graph series -- see the note in
  // useOrgGraphStatsRelay; systemStats computes them on every call too.
  const [stats, setStats] = useState<IDashboardStat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const gqlGranularity = toGqlGranularity(granularity);

  const fetchData = useCallback(() => {
    if (!enabled || !systemId) return;

    setIsLoading(true);
    setError(null);

    const subscription = fetchQuery<UseSystemGraphStatsRelayQuery>(
      relayEnvironment,
      SystemGraphStatsQuery,
      { systemId, startDate, endDate, granularity: gqlGranularity }
    ).subscribe({
      next: (response) => {
        const stats = response.systemStats;
        const rentals = stats?.rentalsByPeriod || [];
        const durations = stats?.averageDurationByPeriod || [];
        const parsed: IDashboardGraph = {
          rentals: transformPeriodStats(rentals, granularity),
          users: transformPeriodStats(stats?.usersByPeriod || [], granularity),
          durations: buildDurations(rentals, durations),
          durationPeriods: transformDurationPeriods(durations, granularity),
        };
        setData(parsed);
        setStats(stats ? toDashboardStat(stats) : null);
        setIsLoading(false);
        setIsFetched(true);
      },
      error: (err: Error) => {
        setError(err);
        setIsLoading(false);
        setIsFetched(true);
      },
    });

    return () => subscription.unsubscribe();
  }, [systemId, startDate, endDate, gqlGranularity, granularity, enabled]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  return { data, stats, isLoading, isFetched, error, refetch: fetchData };
}
